import React, { useCallback, useEffect, useRef, useState } from 'react';

export interface SearchOption {
  id: string;
  label: string;
  subtitle?: string;
}

interface AsyncSearchPickerProps {
  label?: string;
  placeholder?: string;
  value: string;
  selectedLabel?: string;
  onChange: (id: string, option: SearchOption | null) => void;
  onSearch: (query: string) => Promise<SearchOption[]>;
  disabled?: boolean;
  minChars?: number;
  minCharsHint?: string;
  searchingLabel?: string;
  noResultsLabel?: string;
}

export const AsyncSearchPicker: React.FC<AsyncSearchPickerProps> = ({
  label,
  placeholder,
  value,
  selectedLabel,
  onChange,
  onSearch,
  disabled = false,
  minChars = 2,
  minCharsHint,
  searchingLabel = 'Searching...',
  noResultsLabel = 'No results found',
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<SearchOption | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!value) {
      setSelectedOption(null);
      if (!open) setQuery('');
      return;
    }
    if (selectedLabel && (!selectedOption || selectedOption.id !== value)) {
      setSelectedOption({ id: value, label: selectedLabel });
      setQuery(selectedLabel);
    }
  }, [value, selectedLabel, open, selectedOption]);

  const runSearch = useCallback(
    async (term: string) => {
      if (term.trim().length < minChars) {
        setResults([]);
        return;
      }
      try {
        setLoading(true);
        const items = await onSearch(term.trim());
        setResults(items);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [minChars, onSearch],
  );

  useEffect(() => {
    if (!open || selectedOption) return;
    const timer = setTimeout(() => {
      runSearch(query);
    }, 250);
    return () => clearTimeout(timer);
  }, [query, open, selectedOption, runSearch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option: SearchOption) => {
    setSelectedOption(option);
    setQuery(option.label);
    setOpen(false);
    setResults([]);
    onChange(option.id, option);
  };

  const handleClear = () => {
    setSelectedOption(null);
    setQuery('');
    setResults([]);
    setOpen(true);
    onChange('', null);
  };

  const handleInputChange = (next: string) => {
    if (selectedOption) {
      setSelectedOption(null);
      onChange('', null);
    }
    setQuery(next);
    setOpen(true);
  };

  const trimmed = query.trim();
  const showDropdown = open && !disabled && !selectedOption;

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      )}
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          className="w-full px-3 py-2 pr-9 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        {selectedOption && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            title="Clear"
          >
            ×
          </button>
        )}
      </div>

      {showDropdown && (
        <div className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
          {loading ? (
            <div className="px-3 py-2 text-sm text-gray-500">{searchingLabel}</div>
          ) : trimmed.length < minChars ? (
            <div className="px-3 py-2 text-sm text-gray-500">{minCharsHint}</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">{noResultsLabel}</div>
          ) : (
            results.map((option) => (
              <button
                key={option.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(option)}
                className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
              >
                <div className="font-medium text-gray-900">{option.label}</div>
                {option.subtitle && (
                  <div className="text-xs text-gray-500">{option.subtitle}</div>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};
